import { z } from "zod";

/**
 * Public free-snapshot funnel contracts. These routes live under
 * `/api/public/snapshot/*` and are called by anonymous (logged-out)
 * visitors, so the client uses `safeFetchPublic` (no auth header)
 * rather than `safeFetch`. Response shapes mirror the route handlers
 * in `webapp/app/api/public/snapshot/**` and the shared result types
 * in `webapp/lib/free-snapshot/result-types.ts` (which in turn match
 * `FullResult` on the Temporal side, `temporal/src/free-snapshot/
 * scoring.ts`).
 *
 * The deep jsonb blobs (`full`, `teaser`, `observations`,
 * `competitorBrands`) use `.passthrough()` so a future Temporal-side
 * field addition is preserved on the wire instead of being silently
 * stripped by `respondWith` (see the contracts-adoption regression
 * note in api.instructions.md).
 */

export const SnapshotEngine = z.enum([
  "chatgpt",
  "claude",
  "perplexity",
  "gemini",
]);
export type SnapshotEngine = z.infer<typeof SnapshotEngine>;

export const SnapshotObservation = z
  .object({
    prompt: z.string(),
    engine: SnapshotEngine,
    brandMentioned: z.boolean(),
    competitorMentioned: z.boolean(),
  })
  .passthrough();
export type SnapshotObservation = z.infer<typeof SnapshotObservation>;

export const CompetitorBrand = z
  .object({
    name: z.string(),
    count: z.number(),
  })
  .passthrough();
export type CompetitorBrand = z.infer<typeof CompetitorBrand>;

export const EngineScore = z
  .object({
    engine: SnapshotEngine,
    appeared: z.number(),
    total: z.number(),
  })
  .passthrough();
export type EngineScore = z.infer<typeof EngineScore>;

/** Mirrors `SnapshotFullResult` in result-types.ts / Temporal's FullResult. */
export const SnapshotFullResult = z
  .object({
    brandLabel: z.string(),
    prompts: z.array(z.string()),
    observations: z.array(SnapshotObservation),
    appearedCount: z.number(),
    totalCells: z.number(),
    perEngine: z.array(EngineScore),
    competitorBrands: z.array(CompetitorBrand),
    summary: z.string(),
    limitations: z.string(),
  })
  .passthrough();
export type SnapshotFullResult = z.infer<typeof SnapshotFullResult>;

// ── POST /api/public/snapshot/start ───────────────────────────────

export const SnapshotStartRequest = z.object({
  domain: z.string(),
  competitorDomain: z.string().optional(),
});
export type SnapshotStartRequest = z.infer<typeof SnapshotStartRequest>;

export const SnapshotStartResponse = z.object({
  snapshotRunId: z.string(),
  status: z.enum(["completed", "running"]),
  normalizedDomain: z.string(),
  domain: z.string(),
  fromCache: z.boolean(),
});
export type SnapshotStartResponse = z.infer<typeof SnapshotStartResponse>;

// ── GET /api/public/snapshot/[snapshotRunId]/status ────────────────

export const SnapshotPerEngineProgress = z.object({
  engine: SnapshotEngine,
  done: z.number(),
  total: z.number(),
});
export type SnapshotPerEngineProgress = z.infer<
  typeof SnapshotPerEngineProgress
>;

/**
 * The status route returns two 200 shapes keyed on `status`. The
 * `failed` shape carries only the error + domain fields; the
 * running/completed shape carries the live progress payload. Modeled
 * as one object with the progress fields optional so `respondWith`
 * accepts both without stripping (the failed branch simply omits
 * them). Clients narrow on `status === "failed"`.
 */
export const SnapshotStatusResponse = z.object({
  status: z.enum(["running", "completed", "failed"]),
  domain: z.string(),
  normalizedDomain: z.string(),
  // failed branch only
  error: z.string().optional(),
  // running / completed branch
  workflowStage: z.string().nullable().optional(),
  createdAt: z.string().nullable().optional(),
  completedAt: z.string().nullable().optional(),
  prompts: z.array(z.string()).optional(),
  observations: z.array(SnapshotObservation).optional(),
  perEngineProgress: z.array(SnapshotPerEngineProgress).optional(),
  competitorBrands: z.array(CompetitorBrand).optional(),
  full: SnapshotFullResult.nullable().optional(),
});
export type SnapshotStatusResponse = z.infer<typeof SnapshotStatusResponse>;

// ── POST /api/public/snapshot/[snapshotRunId]/email ────────────────

export const SnapshotEmailRequest = z.object({
  email: z.string(),
});
export type SnapshotEmailRequest = z.infer<typeof SnapshotEmailRequest>;

export const SnapshotEmailResponse = z.object({
  ok: z.literal(true),
  message: z.string(),
});
export type SnapshotEmailResponse = z.infer<typeof SnapshotEmailResponse>;

// ── POST /api/public/snapshot/report/open ──────────────────────────

export const SnapshotReportOpenRequest = z.object({
  token: z.string(),
});
export type SnapshotReportOpenRequest = z.infer<
  typeof SnapshotReportOpenRequest
>;

export const SnapshotReportOpenResponse = z.object({
  snapshotRunId: z.string(),
  domain: z.string(),
  normalizedDomain: z.string(),
  competitorDomain: z.string().nullable(),
  // Pre-unlock teaser blob; exact shape is owned by the Temporal
  // scoring step, so pass it through untyped rather than risk strips.
  teaser: z.unknown(),
  full: SnapshotFullResult,
  email: z.string(),
  reportToken: z.string(),
  firstOpen: z.boolean(),
});
export type SnapshotReportOpenResponse = z.infer<
  typeof SnapshotReportOpenResponse
>;
